import React, { useState } from 'react'
import {
  Box,
  Button,
  Flex,
  Input,
  Loading,
  Text,
  useAppTheme,
} from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useWorkerListingSupplyAnalyticsQuery } from 'queries/listings/useWorkerListingSupplyAnalyticsQuery'

type Props = {
  listingId: number
}

export const WorkerIneligibilityReasons = ({ listingId }: Props) => {
  const { colors } = useAppTheme()
  const [workerIdInput, setWorkerIdInput] = useState<string>('')
  const [selectedWorkerId, setSelectedWorkerId] = useState<number | null>(null)

  const { data, isLoading, isError, error, refetch } =
    useWorkerListingSupplyAnalyticsQuery(listingId, selectedWorkerId)

  const parsedError = parseErrorMessage(error)

  const handleCheckEligibility = () => {
    const value = Number(workerIdInput)

    if (value === selectedWorkerId) {
      refetch()
    } else {
      setSelectedWorkerId(value)
    }
  }

  return (
    <>
      <Flex alignItems="center" gap={2} flexWrap="wrap" mb={2}>
        <Flex gap={2}>
          <Input
            name="worker_id"
            placeholder="Worker ID"
            type="number"
            inputMode="numeric"
            value={workerIdInput}
            onChange={(e) => {
              setWorkerIdInput(e.target.value.trim())
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCheckEligibility()
              }
            }}
            required
            style={{ maxWidth: 160 }}
          />
        </Flex>

        <Button
          variant="primary"
          onClick={handleCheckEligibility}
          loading={isLoading}
          disabled={!workerIdInput}
        >
          Check
        </Button>
      </Flex>

      {!selectedWorkerId ? (
        <Text fontSize={2} color="lightText">
          Enter a worker ID to see any reasons they cannot view the listing.
        </Text>
      ) : isLoading ? (
        <Flex minHeight={120} justifyContent="center" alignItems="center">
          <Loading />
        </Flex>
      ) : isError ? (
        <Text color={colors.errors[200]} fontSize={2}>
          {parsedError ?? 'Failed to load worker eligibility.'}
        </Text>
      ) : data?.eligibleCount ? (
        <Text fontSize={2}>
          Worker {selectedWorkerId} can view this listing
        </Text>
      ) : (
        <>
          <Text fontSize={2} mb={2}>
            Worker {selectedWorkerId} cannot view this listing
            {data?.ineligibilityReasons.length
              ? ` · ${data.ineligibilityReasons.length} reason${
                  data.ineligibilityReasons.length === 1 ? '' : 's'
                }`
              : ''}
          </Text>
          {data?.ineligibilityReasons.length ? (
            <Box
              as="ul"
              m={0}
              pl={3}
              color="lightText"
              style={{ listStyleType: 'disc' }}
            >
              {data.ineligibilityReasons.map((reason) => (
                <Box as="li" key={reason} mb={1}>
                  <Text fontSize={1} color="lightText">
                    {reason}
                  </Text>
                </Box>
              ))}
            </Box>
          ) : null}
        </>
      )}
    </>
  )
}
